import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Copy, CheckCircle, Code, Zap, Shield, Globe, Smartphone, Settings, ExternalLink, ChevronRight, Book, Terminal } from 'lucide-react';

const WidgetSDK = () => {
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const copyToClipboard = async (code: string, id: string) => {
    await navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const basicImplementation = `<div id="wayja-widget" 
     data-article-id="unique-article-id" 
     data-title="Will Bitcoin reach $100k by end of 2024?" 
     data-category="Crypto">
</div>

<script src="https://wayja.me/widgets/wayja-widget.js"></script>
<script>
    WayjaWidget.init({
        apiKey: 'your_api_key_here',
        containerId: 'wayja-widget',
        baseUrl: 'https://uyanvkwkikftzpanolmw.supabase.co'
    });
</script>`;

  const configurationOptions = `{
    // Required
    apiKey: 'your_api_key_here',
    containerId: 'wayja-widget',
    baseUrl: 'https://uyanvkwkikftzpanolmw.supabase.co',
    
    // Optional
    theme: 'default',              // Widget theme
    compact: false,                // Compact mode for sidebars
    marketId: 'market-uuid',       // Pre-existing market ID
    userToken: 'jwt-token',         // User JWT for authentication
    debug: false                   // Enable debug logging
}`;

  const htmlAttributes = `<div id="wayja-widget"
     data-article-id="unique-article-identifier"     <!-- Required -->
     data-title="Betting question for this article"  <!-- Required -->
     data-category="Crypto"                          <!-- Recommended -->
     data-user-token="jwt-token-if-available">       <!-- Optional -->
</div>`;

  const features = [
    {
      icon: <Zap className="w-6 h-6 text-wayja-teal" />,
      title: "Real-Time Market Data",
      description: "Live odds, probabilities, and trading volumes with automatic updates every 30 seconds."
    },
    {
      icon: <Shield className="w-6 h-6 text-wayja-coral" />,
      title: "Secure Authentication",
      description: "OAuth 2.0 integration with Wayja platform. Users log in directly to Wayja for financial operations."
    },
    {
      icon: <Smartphone className="w-6 h-6 text-wayja-cyan" />,
      title: "Mobile Responsive",
      description: "Optimized for all device sizes with touch-friendly interfaces and adaptive layouts."
    },
    {
      icon: <Settings className="w-6 h-6 text-wayja-teal" />,
      title: "Easy Integration",
      description: "Simple JavaScript SDK with minimal setup. Just add a script tag and initialize."
    },
    {
      icon: <Globe className="w-6 h-6 text-wayja-coral" />,
      title: "Customizable Theming",
      description: "Match your brand with custom themes and CSS overrides for seamless integration."
    },
    {
      icon: <Code className="w-6 h-6 text-wayja-cyan" />,
      title: "Lightweight",
      description: "Less than 50KB minified and gzipped with lazy loading for optimal performance."
    }
  ];

  const widgetVariants = [
    {
      title: "Standard Widget",
      description: "Full-featured widget for article pages",
      code: `WayjaWidget.init({
    apiKey: 'your_api_key_here',
    containerId: 'wayja-widget',
    baseUrl: 'https://uyanvkwkikftzpanolmw.supabase.co',
    theme: 'default'
});`
    },
    {
      title: "Compact Widget",
      description: "Smaller size optimized for sidebars",
      code: `WayjaWidget.init({
    apiKey: 'your_api_key_here',
    containerId: 'wayja-widget-compact',
    baseUrl: 'https://uyanvkwkikftzpanolmw.supabase.co',
    compact: true
});`
    },
    {
      title: "Pre-existing Market",
      description: "Display a specific market by ID",
      code: `WayjaWidget.init({
    apiKey: 'your_api_key_here',
    containerId: 'wayja-widget',
    baseUrl: 'https://uyanvkwkikftzpanolmw.supabase.co',
    marketId: 'f47ac10b-58cc-4372-a567-0e02b2c3d479'
});`
    }
  ];

  return (
    <div className="min-h-screen bg-wayja-cream">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-wayja-light-teal/30 via-white to-wayja-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="mb-6">
              <span className="px-4 py-2 bg-yellow-100 text-yellow-800 text-sm font-medium rounded-full inline-block mb-4">
                Coming Soon
              </span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-wayja-black mb-6">
              Wayja <span className="text-wayja-teal">Widget SDK</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Embed prediction market functionality into your platform with our lightweight, secure widget SDK.
            </p>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-2xl mx-auto mb-8">
              <div className="flex items-start">
                <Zap className="w-6 h-6 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" />
                <div className="text-left">
                  <h3 className="font-semibold text-yellow-800 mb-2">🚧 Widget SDK In Development</h3>
                  <p className="text-yellow-700 text-sm mb-2">
                    We're currently building the Widget SDK to make it easy to embed prediction markets into your platform. 
                    The SDK will include:
                  </p>
                  <ul className="text-yellow-700 text-sm list-disc list-inside space-y-1 ml-2">
                    <li>Lightweight JavaScript widget for easy integration</li>
                    <li>Real-time market data and odds updates</li>
                    <li>OAuth 2.0 authentication integration</li>
                    <li>Customizable themes and responsive design</li>
                    <li>Mobile-optimized interface</li>
                  </ul>
                  <p className="text-yellow-700 text-sm mt-3">
                    Check back soon or contact us at <a href="mailto:support@wayja.me" className="underline font-medium">support@wayja.me</a> for updates.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/developers/quickstart"
                className="bg-wayja-teal hover:bg-wayja-dark-teal text-white px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <Terminal className="w-5 h-5 mr-2" />
                Quick Start Guide
                <ChevronRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to="/developers/api-reference"
                className="border-2 border-wayja-slate hover:border-wayja-teal text-wayja-dark-slate px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
              >
                <Book className="w-5 h-5 mr-2" />
                API Reference
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Coming Soon Message */}
      <div className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 mb-8">
            <h2 className="text-2xl font-bold text-yellow-800 mb-4">🚧 Widget SDK Coming Soon</h2>
            <p className="text-yellow-700 mb-4">
              We're working hard to bring you an easy-to-use widget SDK for embedding prediction markets into your platform.
            </p>
            <p className="text-yellow-700 text-sm">
              In the meantime, you can use our REST API to integrate prediction market functionality. Check out our <Link to="/developers/api-reference" className="underline font-medium">API Reference</Link> and <Link to="/developers/quickstart" className="underline font-medium">Quick Start Guide</Link> to get started.
            </p>
          </div>
        </div>
      </div>

      {/* Additional Resources */}
      <div className="py-20 bg-wayja-dark-slate text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Get Started with Our API
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              While the Widget SDK is in development, you can use our REST API to integrate prediction markets.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Link
              to="/developers/quickstart"
              className="bg-white/10 hover:bg-white/20 p-6 rounded-xl transition-colors"
            >
              <Terminal className="w-8 h-8 text-wayja-teal mb-4" />
              <h3 className="text-xl font-semibold mb-2">Quick Start Guide</h3>
              <p className="text-wayja-gray mb-4">Step-by-step guide to get started with Wayja integration.</p>
              <span className="text-wayja-light-teal hover:text-wayja-teal font-medium inline-flex items-center">
                Start Guide →
              </span>
            </Link>

            <Link
              to="/developers/api-reference"
              className="bg-white/10 hover:bg-white/20 p-6 rounded-xl transition-colors"
            >
              <Book className="w-8 h-8 text-wayja-coral mb-4" />
              <h3 className="text-xl font-semibold mb-2">API Reference</h3>
              <p className="text-wayja-gray mb-4">Complete documentation for all API endpoints.</p>
              <span className="text-wayja-light-teal hover:text-wayja-teal font-medium inline-flex items-center">
                View API Docs →
              </span>
            </Link>

            <Link
              to="/developers/examples"
              className="bg-white/10 hover:bg-white/20 p-6 rounded-xl transition-colors"
            >
              <Code className="w-8 h-8 text-wayja-cyan mb-4" />
              <h3 className="text-xl font-semibold mb-2">Code Examples</h3>
              <p className="text-wayja-gray mb-4">Ready-to-use code samples and integration examples.</p>
              <span className="text-wayja-light-teal hover:text-wayja-teal font-medium inline-flex items-center">
                View Examples →
              </span>
            </Link>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-r from-wayja-teal to-wayja-dark-teal">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-wayja-light-teal mb-8">
            Use our REST API to integrate prediction markets while the Widget SDK is in development.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/developers/quickstart"
              className="bg-white text-wayja-teal hover:bg-wayja-cream px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              Get Started
              <ChevronRight className="w-5 h-5 ml-2" />
            </Link>
            <a
              href="mailto:support@wayja.me"
              className="border-2 border-white text-white hover:bg-white hover:text-wayja-teal px-8 py-4 rounded-lg font-semibold text-lg transition-colors inline-flex items-center justify-center"
            >
              Contact Support
              <ExternalLink className="w-5 h-5 ml-2" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetSDK;

